<script lang="ts">
	import '$lib/styles/tokens.base.css';
	import '$lib/styles/tokens.valheim.css';
	import '$lib/styles/tokens.palworld.css';
	import '$lib/styles/tokens.zomboid.css';
	import favicon from '$lib/assets/favicon.svg';
	import { page } from '$app/state';
	import { goto } from '$app/navigation';
	import { auth } from '$lib/stores/auth.svelte';
	import Sidebar from '$lib/components/Sidebar.svelte';
	import CommandPalette from '$lib/components/CommandPalette.svelte';
	import ShortcutsOverlay from '$lib/components/ShortcutsOverlay.svelte';

	let { children } = $props();

	let theme = $state<'light' | 'dark' | null>(null);

	$effect(() => {
		try {
			const stored = localStorage.getItem('gamedash-theme');
			if (stored === 'light' || stored === 'dark') theme = stored;
		} catch {
			// stockage indisponible (navigation privée) : on reste sur "système"
		}
		auth.check();
	});

	$effect(() => {
		if (theme === null) {
			delete document.documentElement.dataset.theme;
			try {
				localStorage.removeItem('gamedash-theme');
			} catch {
				/* stockage indisponible */
			}
		} else {
			document.documentElement.dataset.theme = theme;
			try {
				localStorage.setItem('gamedash-theme', theme);
			} catch {
				/* stockage indisponible */
			}
		}
	});

	const game = $derived(
		page.url.pathname.startsWith('/servers/valheim') ||
			page.url.pathname.startsWith('/wiki/valheim')
			? 'valheim'
			: page.url.pathname.startsWith('/servers/palworld') ||
				  page.url.pathname.startsWith('/wiki/palworld')
				? 'palworld'
				: page.url.pathname.startsWith('/servers/zomboid') ||
					  page.url.pathname.startsWith('/wiki/zomboid')
					? 'zomboid'
					: null
	);

	$effect(() => {
		if (game) document.documentElement.dataset.game = game;
		else delete document.documentElement.dataset.game;
	});

	const isLoginPage = $derived(page.url.pathname === '/login');
	// Seule page réellement publique de l'app : la liste des mods à installer
	// pour rejoindre un serveur, faite pour être partagée à des joueurs qui
	// n'ont (et n'ont pas besoin d')un compte gamedash. Traitée comme
	// isLoginPage ci-dessous -- ni redirection vers /login, ni chrome (Sidebar,
	// palette de commandes) autour d'elle.
	// `/play` (l'index, sans slash final) et `/play/<slug>` sont tous les
	// deux publics -- startsWith('/play/') seul ratait l'index lui-même,
	// exactement le chemin canonique désormais partagé aux joueurs.
	const isPublicPage = $derived(
		page.url.pathname === '/play' || page.url.pathname.startsWith('/play/')
	);

	// Garde de réentrance : sans elle, un goto() en cours qui ne s'est pas
	// encore reflété dans page.url.pathname pouvait faire retomber cet effect
	// dans la même branche et rappeler goto() à nouveau dans le même flush —
	// exactement le genre de cycle que Svelte tue avec
	// "effect_update_depth_exceeded" (observé en prod avec plusieurs 401
	// simultanés au montage d'une page).
	let navigatingAuth = false;
	$effect(() => {
		if (!auth.checked || navigatingAuth || isPublicPage) return;
		if (!auth.user && !isLoginPage) {
			navigatingAuth = true;
			goto('/login').finally(() => (navigatingAuth = false));
		} else if (auth.user && isLoginPage) {
			navigatingAuth = true;
			goto('/').finally(() => (navigatingAuth = false));
		}
	});

	// Ctrl/Cmd+K -> palette de commandes, "?" -> liste des raccourcis (sauf
	// dans un champ texte, où "?" doit rester un caractère normal à taper).
	// Écoute globale unique posée ici plutôt que dupliquée par page.
	let paletteOpen = $state(false);
	let shortcutsOpen = $state(false);

	function isTextEntry(target: EventTarget | null) {
		if (!(target instanceof HTMLElement)) return false;
		const tag = target.tagName;
		return tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable;
	}

	function onGlobalKeydown(e: KeyboardEvent) {
		// svelte:window must live at the template's top level (Svelte doesn't
		// allow it inside an {#if} block), so the "only when the authenticated
		// shell is showing" guard lives in the handler itself instead of in
		// where the listener is attached.
		if (!auth.user || isLoginPage) return;
		if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
			e.preventDefault();
			shortcutsOpen = false;
			paletteOpen = true;
		} else if (e.key === '?' && !isTextEntry(e.target) && !paletteOpen && !shortcutsOpen) {
			e.preventDefault();
			shortcutsOpen = true;
		}
	}
</script>

<svelte:window onkeydown={onGlobalKeydown} />

<svelte:head>
	<link rel="icon" href={favicon} />
	<title>gamedash</title>
</svelte:head>

{#if isLoginPage || isPublicPage}
	{@render children()}
{:else if !auth.checked}
	<div class="boot">Chargement…</div>
{:else if auth.user}
	{#if !game}
		<div class="ops-grid-bg" aria-hidden="true"></div>
	{/if}
	<div class="shell">
		<Sidebar
			{theme}
			onthemechange={(t) => (theme = t)}
			onsearch={() => {
				shortcutsOpen = false;
				paletteOpen = true;
			}}
		/>
		<main>
			{@render children()}
		</main>
	</div>
	<CommandPalette bind:open={paletteOpen} />
	<ShortcutsOverlay bind:open={shortcutsOpen} />
{/if}

<style>
	.shell {
		display: flex;
		min-height: 100vh;
	}
	main {
		flex: 1;
		min-width: 0;
		padding: var(--space-6);
	}
	.boot {
		display: flex;
		align-items: center;
		justify-content: center;
		min-height: 100vh;
		color: var(--text-muted);
	}
	@media (max-width: 880px) {
		.shell {
			flex-direction: column;
		}
		main {
			padding: var(--space-4);
		}
	}
</style>
